import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, CheckCircle, Rocket, TrendingUp, Target, Search, Edit3, BarChart3, FileText, Users, DollarSign, Ban, Zap, Star, Grid, ArrowRight, Info, MessageCircle } from 'lucide-react';
import './App.css';
import './GoogleAds.css';
import { waLink, WHATSAPP_URL } from './analytics';

function GoogleAds() {
  const [expanded, setExpanded] = useState({});

  const toggleExpanded = (packageId) => {
    setExpanded(prev => ({
      ...prev,
      [packageId]: !prev[packageId]
    }));
    
    // Smooth scroll to package card when expanding
    setTimeout(() => {
      const element = document.getElementById(`package-${packageId}`);
      if (element && !expanded[packageId]) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const packages = [
    {
      id: 'basic',
      name: 'Google Ads Basic',
      price: '899.000',
      period: 'bulan',
      description: 'Paket dasar untuk mulai beriklan di Google Search dengan pengaturan yang rapi dan monitoring rutin.',
      suitable: [
        'Bisnis yang baru mulai Google Ads',
        'Jasa lokal dan bisnis dengan produk/layanan yang jelas',
        'Kebutuhan lead sederhana dengan target kata kunci utama'
      ],
      features: [
        { icon: Search, text: 'Setup 1 campaign Search' },
        { icon: Target, text: 'Riset keyword' },
        { icon: Edit3, text: 'Copywriting iklan' },
        { icon: BarChart3, text: 'Monitoring mingguan' },
        { icon: FileText, text: 'Laporan bulanan' }
      ],
      conclusion: 'Cocok untuk membangun fondasi campaign yang sehat dan mulai menguji performa.',
      popular: false,
      color: '#10b981',
      gradient: 'from-green-400 to-blue-500',
      icon: Rocket
    },
    {
      id: 'professional',
      name: 'Google Ads Professional',
      price: '1.499.000',
      period: 'bulan',
      description: 'Paket untuk bisnis yang membutuhkan struktur lebih lengkap dan optimasi yang lebih intens untuk meningkatkan efisiensi biaya.',
      suitable: [
        'Bisnis dengan beberapa produk/jasa utama',
        'Brand yang ingin memperbaiki kualitas traffic dan leads',
        'Kebutuhan optimasi CPC dan struktur kata kunci lebih rapi'
      ],
      features: [
        { icon: Grid, text: 'Multi campaign (Search / Display)' },
        { icon: Users, text: 'Riset keyword kompetitor' },
        { icon: DollarSign, text: 'Optimasi CPC' },
        { icon: Ban, text: 'Negative keyword' },
        { icon: BarChart3, text: 'Laporan performa detail' }
      ],
      conclusion: 'Dirancang untuk meningkatkan kualitas dan efisiensi campaign secara konsisten.',
      popular: true,
      color: '#8b5cf6',
      gradient: 'from-purple-400 to-pink-500',
      icon: TrendingUp
    },
    {
      id: 'performance',
      name: 'Google Ads Performance',
      price: '2.499.000',
      period: 'bulan',
      description: 'Paket advanced untuk kebutuhan performa yang lebih agresif, dengan struktur campaign lebih kompleks dan fokus pada optimasi konversi.',
      suitable: [
        'Bisnis yang ingin scaling performa iklan',
        'Brand dengan volume pencarian dan kompetisi yang lebih tinggi',
        'Kebutuhan optimasi konversi dan strategi bidding yang lebih terarah'
      ],
      features: [
        { icon: Grid, text: 'Struktur campaign advanced' },
        { icon: Target, text: 'Optimasi konversi' },
        { icon: Zap, text: 'Strategi bidding' },
        { icon: BarChart3, text: 'Analisa performa mendalam' },
        { icon: Star, text: 'Rekomendasi scaling' }
      ],
      conclusion: 'Cocok untuk bisnis yang ingin mendorong pertumbuhan dengan pendekatan berbasis data.',
      popular: false,
      color: '#f97316',
      gradient: 'from-orange-400 to-red-500',
      icon: Star
    }
  ];

  return (
    <div className="google-ads-page">
      {/* Header Section */}
      <section className="google-ads-hero">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <Target className="badge-icon" size={20} />
              <span className="badge-text">Google Ads Management</span>
            </div>
            <h1 className="hero-title">
              Google Ads
            </h1>
            <p className="hero-subtitle">
              Layanan pengelolaan Google Ads untuk menjangkau calon pelanggan dengan intent tinggi, 
              dengan struktur campaign yang rapi dan optimasi berkala.
            </p>
            <div className="hero-actions">
              <a 
                href={WHATSAPP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <span>Konsultasi Gratis</span>
                <ChevronRight size={20} />
              </a>
              <button 
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-secondary"
              >
                <span>Lihat Paket</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
          <div className="hero-visual">
              <div className="floating-cards">
                <div className="card card-1">
                  <Search size={24} />
                  <span>Search Ads</span>
                </div>
                <div className="card card-2">
                  <BarChart3 size={24} />
                  <span>Performance</span>
                </div>
                <div className="card card-3">
                  <Target size={24} />
                  <span>Targeting</span>
                </div>
              </div>
            </div>
        </div>
      </section>

      {/* Packages Section */}
      <section id="packages" className="packages-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Paket Bulanan</h2>
            <p className="section-subtitle">
              Pilih paket yang sesuai dengan kebutuhan bisnis Anda
            </p>
          </div>

          <div className="packages-grid">
            {packages.map((pkg) => {
              const IconComponent = pkg.icon;
              return (
                <div 
                  key={pkg.id} 
                  id={`package-${pkg.id}`}
                  className={`package-card ${pkg.popular ? 'popular' : ''} ${expanded[pkg.id] ? 'expanded' : ''}`}
                >
                  {pkg.popular && (
                    <div className="popular-badge">
                      <span>POPULER</span>
                    </div>
                  )}
                  
                  <div className="package-header">
                    <div className="package-icon">
                      <div className={`icon-wrapper ${pkg.gradient}`}>
                        <IconComponent size={32} className="text-white" />
                      </div>
                    </div>
                    <h3 className="package-name">{pkg.name}</h3>
                    <div className="package-price">
                      <span className="price">Rp {pkg.price}</span>
                      <span className="period">/{pkg.period}</span>
                    </div>
                    <p className="budget-note">(Budget iklan terpisah)</p>
                    <div className="package-short-description">
                      <p>{pkg.description}</p>
                    </div>
                  </div>

                  <div className="package-body">
                    <div className="package-suitable">
                      <h4>Cocok Untuk</h4>
                      <ul>
                        {pkg.suitable.map((item, index) => (
                          <li key={index}>
                            <CheckCircle size={16} className="text-green-500" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="package-features">
                      <h4>Fitur</h4>
                      <ul>
                        {pkg.features.map((feature, index) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <li key={index}>
                              <FeatureIcon size={16} className="feature-icon" />
                              <span>{feature.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <div className="package-conclusion">
                      <Info size={16} className="text-blue-500" />
                      <p>{pkg.conclusion}</p>
                    </div>
                  </div>

                  <div className="package-footer">
                    <button 
                      className={`btn ${pkg.popular ? 'btn-primary' : 'btn-outline'} ${expanded[pkg.id] ? 'expanded' : ''}`}
                      onClick={() => toggleExpanded(pkg.id)}
                    >
                      <span>{expanded[pkg.id] ? 'Sembunyikan Detail' : 'Lihat Detail'}</span>
                      <ChevronRight size={16} className={`chevron ${expanded[pkg.id] ? 'rotated' : ''}`} />
                    </button>
                    <a 
                      href={waLink(`Halo, saya ingin memilih paket Google Ads: ${pkg.name}. Mohon info lebih lanjut.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-whatsapp"
                    >
                      <MessageCircle size={16} />
                      <span>Pilih Paket</span>
                      <ChevronRight size={16} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Note Section */}
      <section className="note-section">
        <div className="container">
          <div className="note-card">
            <div className="note-icon">
              <Info size={32} className="text-blue-500" />
            </div>
            <div className="note-content">
              <h3>Catatan Penting</h3>
              <p>
                Budget iklan tidak termasuk biaya jasa pengelolaan. 
                Biaya yang tercantum adalah untuk layanan setup, monitoring, dan optimasi campaign Google Ads Anda.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          {/* Floating Elements */}
          <div className="cta-floating-elements">
            <div className="floating-element element-1">
              <Target size={24} />
            </div>
            <div className="floating-element element-2">
              <TrendingUp size={24} />
            </div>
            <div className="floating-element element-3">
              <Rocket size={24} />
            </div>
          </div>
          
          <div className="cta-content">
            <div className="cta-badge">
              <span>Mulai Sekarang</span>
            </div>
            <h2>Siap Meningkatkan Performa Iklan Anda?</h2>
            <p>
              Konsultasikan kebutuhan Google Ads Anda dengan tim profesional kami dan dapatkan strategi yang tepat untuk bisnis Anda.
            </p>
            <div className="cta-actions">
              <a 
                href={WHATSAPP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
              >
                <span>WhatsApp Konsultasi</span>
                <ChevronRight size={20} />
              </a>
              <Link to="/pricing" className="btn btn-outline btn-large">
                <span>Lihat Layanan Lain</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default GoogleAds;
